"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PersistedEvalOutputDefinitionSchema = exports.CategoricalEvalOutputDefinitionV2Schema = exports.BooleanEvalOutputDefinitionV2Schema = exports.NumericEvalOutputDefinitionV2Schema = exports.MinimumCategoricalCategoryCount = exports.LegacyEvalOutputDefinitionSchema = exports.EvalOutputDataTypeSchema = void 0;
exports.getMinimumCategoricalCategoriesMessage = getMinimumCategoricalCategoriesMessage;
exports.getCategoricalCategoryRuleViolations = getCategoricalCategoryRuleViolations;
exports.resolvePersistedEvalOutputDefinition = resolvePersistedEvalOutputDefinition;
exports.createNumericEvalOutputDefinition = createNumericEvalOutputDefinition;
exports.createBooleanEvalOutputDefinition = createBooleanEvalOutputDefinition;
exports.createCategoricalEvalOutputDefinition = createCategoricalEvalOutputDefinition;
exports.buildEvalOutputResultSchema = buildEvalOutputResultSchema;
exports.compilePersistedEvalOutputDefinition = compilePersistedEvalOutputDefinition;
exports.validateEvalOutputResult = validateEvalOutputResult;
const zod_1 = __importDefault(require("zod"));
const scores_1 = require("../../domain/scores");
exports.EvalOutputDataTypeSchema = zod_1.default.enum([
    scores_1.ScoreDataTypeEnum.NUMERIC,
    scores_1.ScoreDataTypeEnum.CATEGORICAL,
    scores_1.ScoreDataTypeEnum.BOOLEAN,
]);
// Legacy evaluator templates stored score/reasoning prompts directly as strings.
// Keep this permissive so older rows can still be parsed and normalized.
exports.LegacyEvalOutputDefinitionSchema = zod_1.default.object({
    reasoning: zod_1.default.string().default(""),
    score: zod_1.default.string().default(""),
});
const EvalOutputFieldDefinitionSchema = zod_1.default.object({
    description: zod_1.default.string().trim().min(1),
});
exports.MinimumCategoricalCategoryCount = 2;
function getMinimumCategoricalCategoriesMessage() {
    return `Add at least ${exports.MinimumCategoricalCategoryCount} categories`;
}
function getCategoricalCategoryRuleViolations(categories) {
    const violations = [];
    if (categories.length < exports.MinimumCategoricalCategoryCount) {
        violations.push({
            type: "minimum_count",
            minimumCount: exports.MinimumCategoricalCategoryCount,
        });
    }
    const seenValues = new Set();
    categories.forEach((category, index) => {
        const normalizedValue = category.trim();
        if (seenValues.has(normalizedValue)) {
            violations.push({
                type: "duplicate_value",
                index,
            });
            return;
        }
        seenValues.add(normalizedValue);
    });
    return violations;
}
const EvalCategoricalCategorySchema = zod_1.default.string().trim().min(1);
const NumericEvalOutputScoreDefinitionSchema = EvalOutputFieldDefinitionSchema.extend({
    minValue: zod_1.default.number().optional(),
    maxValue: zod_1.default.number().optional(),
}).refine(({ minValue, maxValue }) => minValue === undefined || maxValue === undefined || minValue <= maxValue, {
    message: "Minimum value must be less than or equal to maximum value",
});
exports.NumericEvalOutputDefinitionV2Schema = zod_1.default.object({
    version: zod_1.default.literal(2),
    dataType: zod_1.default.literal(scores_1.ScoreDataTypeEnum.NUMERIC),
    reasoning: EvalOutputFieldDefinitionSchema,
    score: NumericEvalOutputScoreDefinitionSchema,
});
exports.BooleanEvalOutputDefinitionV2Schema = zod_1.default.object({
    version: zod_1.default.literal(2),
    dataType: zod_1.default.literal(scores_1.ScoreDataTypeEnum.BOOLEAN),
    reasoning: EvalOutputFieldDefinitionSchema,
    score: EvalOutputFieldDefinitionSchema,
});
exports.CategoricalEvalOutputDefinitionV2Schema = zod_1.default
    .object({
    version: zod_1.default.literal(2),
    dataType: zod_1.default.literal(scores_1.ScoreDataTypeEnum.CATEGORICAL),
    reasoning: EvalOutputFieldDefinitionSchema,
    score: zod_1.default.object({
        description: zod_1.default.string().trim().min(1),
        categories: zod_1.default.array(EvalCategoricalCategorySchema),
        shouldAllowMultipleMatches: zod_1.default.boolean().default(false),
    }),
})
    .superRefine((value, ctx) => {
    getCategoricalCategoryRuleViolations(value.score.categories).forEach((violation) => {
        switch (violation.type) {
            case "minimum_count":
                ctx.addIssue({
                    code: "custom",
                    message: getMinimumCategoricalCategoriesMessage(),
                    path: ["score", "categories"],
                });
                return;
            case "duplicate_value":
                ctx.addIssue({
                    code: "custom",
                    message: "Categories must be unique",
                    path: ["score", "categories", violation.index],
                });
                return;
        }
    });
});
exports.PersistedEvalOutputDefinitionSchema = zod_1.default.union([
    exports.LegacyEvalOutputDefinitionSchema,
    exports.NumericEvalOutputDefinitionV2Schema,
    exports.BooleanEvalOutputDefinitionV2Schema,
    exports.CategoricalEvalOutputDefinitionV2Schema,
]);
// Resolve the persisted evaluator output definition into one stable execution
// shape regardless of whether the source row is legacy or v2.
function resolvePersistedEvalOutputDefinition(outputDefinition) {
    if (!("version" in outputDefinition)) {
        return {
            dataType: scores_1.ScoreDataTypeEnum.NUMERIC,
            reasoningDescription: outputDefinition.reasoning,
            scoreDescription: outputDefinition.score,
        };
    }
    if (outputDefinition.dataType === scores_1.ScoreDataTypeEnum.NUMERIC) {
        return {
            dataType: outputDefinition.dataType,
            reasoningDescription: outputDefinition.reasoning.description,
            scoreDescription: outputDefinition.score.description,
            minValue: outputDefinition.score.minValue,
            maxValue: outputDefinition.score.maxValue,
        };
    }
    if (outputDefinition.dataType === scores_1.ScoreDataTypeEnum.BOOLEAN) {
        return {
            dataType: outputDefinition.dataType,
            reasoningDescription: outputDefinition.reasoning.description,
            scoreDescription: outputDefinition.score.description,
        };
    }
    return {
        dataType: scores_1.ScoreDataTypeEnum.CATEGORICAL,
        reasoningDescription: outputDefinition.reasoning.description,
        scoreDescription: outputDefinition.score.description,
        categories: outputDefinition.score.categories,
        shouldAllowMultipleMatches: outputDefinition.score.shouldAllowMultipleMatches,
    };
}
function createNumericEvalOutputDefinition(params) {
    return exports.NumericEvalOutputDefinitionV2Schema.parse({
        version: 2,
        dataType: scores_1.ScoreDataTypeEnum.NUMERIC,
        reasoning: {
            description: params.reasoningDescription,
        },
        score: {
            description: params.scoreDescription,
            ...(params.minValue !== undefined ? { minValue: params.minValue } : {}),
            ...(params.maxValue !== undefined ? { maxValue: params.maxValue } : {}),
        },
    });
}
function createBooleanEvalOutputDefinition(params) {
    return exports.BooleanEvalOutputDefinitionV2Schema.parse({
        version: 2,
        dataType: scores_1.ScoreDataTypeEnum.BOOLEAN,
        reasoning: {
            description: params.reasoningDescription,
        },
        score: {
            description: params.scoreDescription,
        },
    });
}
function createCategoricalEvalOutputDefinition(params) {
    return exports.CategoricalEvalOutputDefinitionV2Schema.parse({
        version: 2,
        dataType: scores_1.ScoreDataTypeEnum.CATEGORICAL,
        reasoning: {
            description: params.reasoningDescription,
        },
        score: {
            description: params.scoreDescription,
            categories: params.categories,
            shouldAllowMultipleMatches: params.shouldAllowMultipleMatches ?? false,
        },
    });
}
function buildResultSchemaForResolvedOutputDefinition(resolvedOutputDefinition) {
    const reasoningSchema = zod_1.default
        .string()
        .describe(resolvedOutputDefinition.reasoningDescription);
    if (resolvedOutputDefinition.dataType === scores_1.ScoreDataTypeEnum.CATEGORICAL) {
        const [firstCategory, ...remainingCategories] = resolvedOutputDefinition.categories;
        if (!firstCategory) {
            throw new Error("Categorical eval output definition requires at least one category");
        }
        const categoricalValueSchema = zod_1.default.enum([
            firstCategory,
            ...remainingCategories,
        ]);
        const scoreSchema = resolvedOutputDefinition.shouldAllowMultipleMatches
            ? zod_1.default
                .array(categoricalValueSchema)
                .min(1)
                .max(remainingCategories.length + 1)
                .superRefine((categories, ctx) => {
                if (new Set(categories).size !== categories.length) {
                    ctx.addIssue({
                        code: "custom",
                        message: "Score categories must be unique",
                    });
                }
            })
            : categoricalValueSchema;
        return zod_1.default.object({
            reasoning: reasoningSchema,
            score: scoreSchema.describe(resolvedOutputDefinition.scoreDescription),
        });
    }
    if (resolvedOutputDefinition.dataType === scores_1.ScoreDataTypeEnum.BOOLEAN) {
        return zod_1.default.object({
            reasoning: reasoningSchema,
            score: zod_1.default.boolean().describe(resolvedOutputDefinition.scoreDescription),
        });
    }
    let scoreSchema = zod_1.default.number();
    if (resolvedOutputDefinition.minValue !== undefined) {
        scoreSchema = scoreSchema.min(resolvedOutputDefinition.minValue);
    }
    if (resolvedOutputDefinition.maxValue !== undefined) {
        scoreSchema = scoreSchema.max(resolvedOutputDefinition.maxValue);
    }
    return zod_1.default.object({
        reasoning: reasoningSchema,
        score: scoreSchema.describe(resolvedOutputDefinition.scoreDescription),
    });
}
function buildEvalOutputResultSchema(outputDefinition) {
    return buildResultSchemaForResolvedOutputDefinition(resolvePersistedEvalOutputDefinition(outputDefinition));
}
function compilePersistedEvalOutputDefinition(outputDefinition) {
    const resolvedOutputDefinition = resolvePersistedEvalOutputDefinition(outputDefinition);
    const outputResultSchema = buildResultSchemaForResolvedOutputDefinition(resolvedOutputDefinition);
    return {
        resolvedOutputDefinition,
        outputResultSchema,
    };
}
function normalizeValidatedEvalOutputResult(result, resolvedOutputDefinition) {
    if (resolvedOutputDefinition.dataType === scores_1.ScoreDataTypeEnum.NUMERIC) {
        return {
            dataType: scores_1.ScoreDataTypeEnum.NUMERIC,
            score: result.score,
            reasoning: result.reasoning,
        };
    }
    if (resolvedOutputDefinition.dataType === scores_1.ScoreDataTypeEnum.BOOLEAN) {
        return {
            dataType: scores_1.ScoreDataTypeEnum.BOOLEAN,
            score: result.score,
            reasoning: result.reasoning,
        };
    }
    return {
        dataType: scores_1.ScoreDataTypeEnum.CATEGORICAL,
        matches: Array.isArray(result.score)
            ? result.score
            : [result.score],
        reasoning: result.reasoning,
    };
}
function validateEvalOutputResult(params) {
    const result = params.compiledOutputDefinition.outputResultSchema.safeParse(params.response);
    if (result.success) {
        return {
            success: true,
            data: normalizeValidatedEvalOutputResult(result.data, params.compiledOutputDefinition.resolvedOutputDefinition),
        };
    }
    return { success: false, error: result.error.message };
}
